import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

const REPORT_TYPE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'incident', label: 'Incident' },
];

const SECTION_TYPE_OPTIONS = [
  { value: 'monitor_notes', label: 'Monitor Notes' },
  { value: 'incidents', label: 'Incidents' },
  { value: 'recommendations', label: 'Recommendations' },
];

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.mp4';
const MAX_FILES = 5;

interface FilePreview {
  file: File;
  previewUrl: string | null;
}

interface Section {
  heading: string;
  content: string;
  type: string;
}

export default function EditReportPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [reportDate, setReportDate] = useState('');
  const [reportType, setReportType] = useState('daily');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [metricsExpanded, setMetricsExpanded] = useState(false);
  const [totalAlerts, setTotalAlerts] = useState<string>('');
  const [employeesSeen, setEmployeesSeen] = useState<string>('');
  const [incidentsCount, setIncidentsCount] = useState<string>('');
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    try {
      const res = await api.get(`/reports/${reportId}`);
      const r = res.data;
      setStoreId(r.store_id);
      setReportDate(r.report_date);
      setReportType(r.report_type);
      setTitle(r.title);
      setSummary(r.summary || '');
      setSections(r.sections || []);
      setExistingAttachments(r.attachments || []);
      if (r.metrics) {
        const m = r.metrics;
        if (m.total_alerts !== undefined) setTotalAlerts(String(m.total_alerts));
        if (m.employees_seen !== undefined) setEmployeesSeen(String(m.employees_seen));
        if (m.incidents_count !== undefined) setIncidentsCount(String(m.incidents_count));
        setMetricsExpanded(true);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load report.'));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  function updateSection(index: number, field: 'heading' | 'content', value: string) {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }

  function addSection() {
    setSections((prev) => [
      ...prev,
      { heading: '', content: '', type: 'monitor_notes' },
    ]);
  }

  function removeSection(index: number) {
    setSections((prev) => prev.filter((_, i) => i !== index));
  }

  function updateSectionType(index: number, type: string) {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, type } : s)));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files;
    if (!selected) return;

    const incoming = Array.from(selected);
    const totalCount = existingAttachments.length + files.length + incoming.length;
    if (totalCount > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }

    const newPreviews: FilePreview[] = incoming.map((file) => ({
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...newPreviews]);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeFile(index: number) {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }

  function removeExistingAttachment(index: number) {
    setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(status: 'draft' | 'published') {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // 1. Upload new files
      const newKeys: string[] = [];
      for (const fp of files) {
        const formData = new FormData();
        formData.append('file', fp.file);
        const res = await api.post('/upload', formData);
        const body = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        newKeys.push(body.s3_key);
      }

      // 2. Build metrics
      const metrics: Record<string, number> = {};
      if (totalAlerts !== '') metrics.total_alerts = Number(totalAlerts);
      if (employeesSeen !== '') metrics.employees_seen = Number(employeesSeen);
      if (incidentsCount !== '') metrics.incidents_count = Number(incidentsCount);

      // 3. Update report
      const payload = {
        report_date: reportDate,
        report_type: reportType,
        title: title.trim(),
        summary: summary.trim(),
        sections: sections.map((s) => ({
          heading: s.heading.trim(),
          content: s.content.trim(),
          type: s.type,
        })),
        metrics: Object.keys(metrics).length > 0 ? metrics : null,
        attachments: [...existingAttachments, ...newKeys],
        status,
      };

      await api.put(`/reports/${reportId}`, payload);
      navigate(`/reports/${reportId}`);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update report.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <p style={styles.loadingText}>Loading report...</p>
        </main>
      </div>
    );
  }

  const totalAttachments = existingAttachments.length + files.length;

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(`/reports/${reportId}`)} style={styles.backButton}>
          ← Back to Report
        </button>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Edit Report</h2>

          {error && <div style={styles.errorBanner}>{error}</div>}

          <div style={styles.form}>
            {/* Date & Type */}
            <div style={styles.row}>
              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Report Date</label>
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  disabled={submitting}
                  style={styles.input}
                />
              </div>
              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Report Type</label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  disabled={submitting}
                  style={styles.select}
                >
                  {REPORT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Title */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Report title"
                disabled={submitting}
                style={styles.input}
              />
            </div>

            {/* Summary */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="High-level summary of operations..."
                disabled={submitting}
                rows={3}
                style={styles.textarea}
              />
            </div>

            {/* Sections */}
            <div style={styles.fieldGroup}>
              <div style={styles.sectionHeader}>
                <label style={styles.label}>Sections</label>
                <button
                  type="button"
                  onClick={addSection}
                  disabled={submitting}
                  style={styles.addSectionButton}
                >
                  + Add Section
                </button>
              </div>

              <div style={styles.sectionsList}>
                {sections.map((section, i) => (
                  <div key={i} style={styles.sectionItem}>
                    <div style={styles.sectionItemHeader}>
                      <input
                        type="text"
                        value={section.heading}
                        onChange={(e) => updateSection(i, 'heading', e.target.value)}
                        placeholder="Section heading"
                        disabled={submitting}
                        style={styles.sectionHeadingInput}
                      />
                      <select
                        value={section.type}
                        onChange={(e) => updateSectionType(i, e.target.value)}
                        disabled={submitting}
                        style={styles.sectionTypeSelect}
                      >
                        <option value="overview">Overview</option>
                        <option value="alerts_summary">Alerts Summary</option>
                        <option value="employee_activity">Employee Activity</option>
                        {SECTION_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {i > 0 && (
                        <button
                          type="button"
                          onClick={() => removeSection(i)}
                          disabled={submitting}
                          style={styles.removeSectionButton}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <textarea
                      value={section.content}
                      onChange={(e) => updateSection(i, 'content', e.target.value)}
                      placeholder="Section content..."
                      disabled={submitting}
                      rows={3}
                      style={styles.textarea}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div style={styles.fieldGroup}>
              <button
                type="button"
                onClick={() => setMetricsExpanded(!metricsExpanded)}
                style={styles.metricsToggle}
              >
                <span style={styles.metricsToggleIcon}>{metricsExpanded ? '▾' : '▸'}</span>
                Metrics (optional)
              </button>

              {metricsExpanded && (
                <div style={styles.metricsGrid}>
                  <div style={styles.metricField}>
                    <label style={styles.metricLabel}>Total Alerts</label>
                    <input
                      type="number"
                      min="0"
                      value={totalAlerts}
                      onChange={(e) => setTotalAlerts(e.target.value)}
                      disabled={submitting}
                      placeholder="0"
                      style={styles.metricInput}
                    />
                  </div>
                  <div style={styles.metricField}>
                    <label style={styles.metricLabel}>Employees Seen</label>
                    <input
                      type="number"
                      min="0"
                      value={employeesSeen}
                      onChange={(e) => setEmployeesSeen(e.target.value)}
                      disabled={submitting}
                      placeholder="0"
                      style={styles.metricInput}
                    />
                  </div>
                  <div style={styles.metricField}>
                    <label style={styles.metricLabel}>Incidents Count</label>
                    <input
                      type="number"
                      min="0"
                      value={incidentsCount}
                      onChange={(e) => setIncidentsCount(e.target.value)}
                      disabled={submitting}
                      placeholder="0"
                      style={styles.metricInput}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Current Attachments</label>
                <div style={styles.fileList}>
                  {existingAttachments.map((key, i) => (
                    <div key={i} style={styles.fileItem}>
                      <span style={styles.attachmentIcon}>📎</span>
                      <span style={styles.fileName}>{key.split('/').pop() || key}</span>
                      <button
                        type="button"
                        onClick={() => removeExistingAttachment(i)}
                        disabled={submitting}
                        style={styles.removeFile}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Attachments */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Add Attachments
                <span style={styles.labelHint}> (max {MAX_FILES} total — JPG, PNG, MP4)</span>
              </label>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || totalAttachments >= MAX_FILES}
                style={{
                  ...styles.uploadButton,
                  opacity: totalAttachments >= MAX_FILES ? 0.5 : 1,
                }}
              >
                Choose Files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {files.length > 0 && (
                <div style={styles.fileList}>
                  {files.map((fp, i) => (
                    <div key={i} style={styles.fileItem}>
                      {fp.previewUrl ? (
                        <img src={fp.previewUrl} alt={fp.file.name} style={styles.thumbnail} />
                      ) : (
                        <div style={styles.videoIcon}>▶</div>
                      )}
                      <span style={styles.fileName}>{fp.file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        disabled={submitting}
                        style={styles.removeFile}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                onClick={() => navigate(`/reports/${reportId}`)}
                disabled={submitting}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSubmit('draft')}
                disabled={submitting}
                style={styles.draftButton}
              >
                {submitting ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="button"
                onClick={() => handleSubmit('published')}
                disabled={submitting}
                style={styles.publishButton}
              >
                {submitting ? 'Publishing...' : 'Publish Report'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--bg-base)' },
  main: { maxWidth: 720, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' },
  backButton: {
    background: 'none', border: 'none', color: 'var(--accent-text)',
    fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer', padding: 0, marginBottom: 'var(--space-6)',
  },
  loadingText: { color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' },
  card: {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-subtle)',
    padding: '32px 32px 28px',
  },
  cardTitle: { fontSize: 'var(--text-xl)', fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', margin: '0 0 24px' },
  errorBanner: {
    background: 'var(--status-critical-muted)', color: 'var(--status-critical)', padding: '12px 16px',
    borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)', marginBottom: 'var(--space-5)',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' },
  row: { display: 'flex', gap: 'var(--space-4)' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' },
  labelHint: { fontWeight: 400, color: 'var(--text-tertiary)' },
  input: {
    padding: '10px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-primary)', colorScheme: 'dark', transition: 'border-color 0.2s',
  },
  textarea: {
    padding: '10px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', outline: 'none', resize: 'vertical',
    background: 'var(--input-bg)', color: 'var(--text-primary)', colorScheme: 'dark', transition: 'border-color 0.2s',
  },
  select: {
    padding: '10px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-primary)', cursor: 'pointer',
    appearance: 'auto' as any, colorScheme: 'dark', transition: 'border-color 0.2s',
  },
  // Sections
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  addSectionButton: {
    padding: '6px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)', fontSize: 'var(--text-sm)', fontWeight: 500, fontFamily: 'var(--font-body)',
    color: 'var(--accent-text)', cursor: 'pointer',
  },
  sectionsList: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionItem: {
    background: 'var(--bg-surface-raised)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)',
    padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
  },
  sectionItemHeader: { display: 'flex', gap: 10, alignItems: 'center' },
  sectionHeadingInput: {
    flex: 1, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-primary)', colorScheme: 'dark',
  },
  sectionTypeSelect: {
    padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-sm)', fontFamily: 'var(--font-body)', outline: 'none',
    background: 'var(--input-bg)', color: 'var(--text-secondary)', cursor: 'pointer',
    appearance: 'auto' as any, colorScheme: 'dark',
  },
  removeSectionButton: {
    background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 16,
    cursor: 'pointer', padding: '2px 6px', borderRadius: 'var(--radius-sm)', lineHeight: 1,
  },
  // Metrics
  metricsToggle: {
    display: 'flex', alignItems: 'center', gap: 6, background: 'none',
    border: 'none', fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)', cursor: 'pointer', padding: 0,
  },
  metricsToggleIcon: { fontSize: 12, color: 'var(--text-tertiary)' },
  metricsGrid: { display: 'flex', gap: 16, marginTop: 4 },
  metricField: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  metricLabel: { fontSize: 'var(--text-sm)', fontWeight: 500, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' },
  metricInput: {
    padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
    fontSize: 'var(--text-md)', fontFamily: 'var(--font-mono)', outline: 'none', width: '100%',
    boxSizing: 'border-box', background: 'var(--input-bg)', color: 'var(--text-primary)', colorScheme: 'dark',
  },
  // File upload
  attachmentIcon: { fontSize: 14 },
  uploadButton: {
    alignSelf: 'flex-start', padding: '12px 20px', borderRadius: 'var(--radius-lg)',
    border: '1px dashed var(--border-default)', background: 'transparent', fontSize: 'var(--text-base)',
    fontWeight: 500, fontFamily: 'var(--font-body)', color: 'var(--text-tertiary)', cursor: 'pointer',
  },
  fileList: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 },
  fileItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
    background: 'var(--bg-surface-raised)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)',
  },
  thumbnail: { width: 40, height: 40, objectFit: 'cover', borderRadius: 'var(--radius-sm)' },
  videoIcon: {
    width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'var(--bg-surface-overlay)',
    color: 'var(--text-primary)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fileName: {
    flex: 1, fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: 'var(--text-primary)', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  removeFile: {
    background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 14,
    cursor: 'pointer', padding: '2px 6px', borderRadius: 'var(--radius-sm)',
  },
  // Actions
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelButton: {
    padding: '10px 22px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)', fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)', cursor: 'pointer',
  },
  draftButton: {
    padding: '10px 22px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-overlay)', fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)', cursor: 'pointer',
  },
  publishButton: {
    padding: '10px 24px', borderRadius: 'var(--radius-lg)', border: 'none',
    background: 'var(--accent)', color: '#fff', fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
};
